// Content for the multi-page marketing site. Every page is a hero plus a list
// of typed blocks, so the copy lives here once and the components only decide
// how it looks. Facts come from fitted.co, the product's backend (detection
// signals, payout terms) and the OAuth scopes app.fitted.co
// requests. No new numbers or claims.

export const CONTACT_EMAIL = "adam@fitted.co";
export const COMPANY = "Fitted AI, Inc.";
export const ADDRESS = ["24651 La Cresta Dr, Unit A", "Dana Point, CA 92629, USA"];
export const APP_LOGIN = "https://app.fitted.co/login";

export type NavItem = { label: string; href: string; line?: string };
export type Section = "product" | "solutions" | "company" | "legal";

export const nav: Record<Section, NavItem[]> = {
  product: [
    { label: "Platform overview", href: "/platform", line: "How the pieces fit together" },
    { label: "Computer vision", href: "/platform/computer-vision", line: "Find your brand in every frame" },
    { label: "Campaign manager", href: "/platform/campaign-manager", line: "Brands, campaigns, creators and posts" },
    { label: "Creator payouts", href: "/platform/creator-payouts", line: "Pay on verified exposure" },
    { label: "Integrations", href: "/platform/integrations", line: "Instagram, TikTok and YouTube" },
  ],
  solutions: [
    { label: "For brands", href: "/solutions/brands", line: "Pay for visibility, not posts" },
    { label: "For agencies", href: "/solutions/agencies", line: "Every client in one account" },
    { label: "For creators", href: "/solutions/creators", line: "Get paid for each second on screen" },
  ],
  company: [
    { label: "About", href: "/about", line: "Who we are and what we build" },
    { label: "Contact", href: "/contact", line: "Email, address and support" },
    { label: "Request access", href: "/request-access", line: "Brands, agencies and creators" },
  ],
  legal: [
    { label: "Privacy Policy", href: "/privacy-policy" },
    { label: "Terms of Service", href: "/terms-of-service" },
  ],
};

export const sectionTitle: Record<Section, string> = {
  product: "Product",
  solutions: "Solutions",
  company: "Company",
  legal: "Legal",
};

export const tagline = "The platform that ties creator payouts to verified brand exposure.";

/* Detection shots already published on fitted.co, with their box data. */
export type Box = { label: string; logo: boolean; left: number; top: number; width: number; height: number; below?: boolean };
export type Corner = "bl" | "br" | "tl" | "tr";
export type Shot = { img: string; alt: string; brand: string; seconds: string; views: string; payout: string; boxes: Box[]; widgetAt: Corner };
export type ShotId = "puma" | "gatorade" | "adidas" | "creator";

export const shots: Record<ShotId, Shot> = {
  puma: {
    img: "/Revised Photo for Homepage.png", alt: "Creator in a yellow Puma tracksuit holding a Yeti tumbler",
    brand: "Puma", seconds: "12.4", views: "150k", payout: "$450.00", widgetAt: "bl",
    boxes: [
      { label: "SWEATSHIRT", logo: false, left: 30, top: 10, width: 40, height: 23 },
      { label: "PANTS", logo: false, left: 30, top: 35, width: 48, height: 52 },
      { label: "YETI TUMBLER", logo: false, left: 72, top: 63, width: 20, height: 22 },
      { label: "PUMA LOGO", logo: true, left: 45, top: 15.5, width: 11, height: 7 },
      { label: "PUMA LOGO", logo: true, left: 58.5, top: 40.5, width: 11, height: 6.5 },
      { label: "YETI LOGO", logo: true, left: 75, top: 74, width: 15, height: 6 },
    ],
  },
  gatorade: {
    img: "/request-access/brand.jpg", alt: "Triathlete running past a Gatorade flag and barrier",
    brand: "Gatorade", seconds: "4.3", views: "38k", payout: "$114.00", widgetAt: "br",
    boxes: [
      { label: "GATORADE FLAG", logo: false, left: 19.75, top: 0, width: 13.5, height: 48.6, below: true },
      { label: "GATORADE LOGO", logo: true, left: 22.5, top: 30, width: 10.8, height: 11.4, below: true },
      { label: "GATORADE LOGO", logo: true, left: 4, top: 48, width: 13.5, height: 18.6, below: true },
    ],
  },
  adidas: {
    img: "/request-access/talent.jpg", alt: "Man in an Adidas Originals track jacket",
    brand: "Adidas", seconds: "5.9", views: "58k", payout: "$174.00", widgetAt: "br",
    boxes: [
      { label: "ADIDAS JACKET", logo: false, left: 12.5, top: 33.5, width: 57.5, height: 66.5 },
      { label: "ADIDAS LOGO", logo: true, left: 24.75, top: 63, width: 16.25, height: 12.5, below: true },
    ],
  },
  creator: {
    img: "/request-access/creator.jpg", alt: "Creator celebrating at a race event beside a Gatorade tent",
    brand: "Gatorade", seconds: "5.6", views: "64k", payout: "$192.00", widgetAt: "tl",
    boxes: [
      { label: "GATORADE TENT", logo: false, left: 0, top: 51.6, width: 29.3, height: 30 },
      { label: "GATORADE LOGO", logo: true, left: 2, top: 81.6, width: 27.3, height: 8.4 },
      { label: "NIKE SHORTS", logo: false, left: 29.3, top: 66.6, width: 35.7, height: 26 },
      { label: "NIKE SWOOSH", logo: true, left: 56.75, top: 86.2, width: 4.5, height: 2.3 },
    ],
  },
};

/* A shot in the shape RoleDetectionCard takes, the card the Request Access page uses. */
export function roleVisual(id: ShotId) {
  const s = shots[id];
  return {
    image: s.img,
    alt: s.alt,
    boxes: s.boxes.map(({ below, ...b }) => ({ ...b, tagBelow: below })),
    widget: { seconds: s.seconds, views: s.views, payout: s.payout },
    widgetAt: s.widgetAt,
  };
}

export const proof = [
  { v: "10M+", l: "Frames analyzed" },
  { v: "97%+", l: "Detection accuracy" },
  { v: "Up to 95%", l: "Lower organic CPMs than traditional creator campaigns" },
];

/* Numbers from the public demo workspace at app.fitted.co/demo. */
export const demoWorkspace = {
  brand: "Gatorade",
  stats: [
    { v: "2", l: "Campaigns" }, { v: "34", l: "Creators" }, { v: "53.7k", l: "Views" },
    { v: "24", l: "Engagement" }, { v: "2", l: "Posts" }, { v: "$161.52", l: "EMV" },
  ],
};

/* What Fitted can read from each platform, in plain words. Matches the OAuth
   scopes app.fitted.co requests (frontend useUrls.ts). */
export type Platform = { id: "tiktok" | "instagram" | "youtube"; name: string; logo: string; sees: string[] };
export const platforms: Platform[] = [
  { id: "tiktok", name: "TikTok", logo: "/logos/tiktok.svg",
    sees: ["Your name, photo and profile link", "Follower and like counts", "Your public videos and their views"] },
  { id: "instagram", name: "Instagram", logo: "/logos/instagram.svg",
    sees: ["Your profile and the posts you publish", "Views and engagement on those posts"] },
  { id: "youtube", name: "YouTube", logo: "/logos/youtube.svg",
    sees: ["Your channel and videos", "Views on your campaign videos"] },
];

export const disconnectLine =
  "Disconnect any time from your Fitted profile or the platform's settings.";

/* ---------- Page model ---------- */

export type Cta = { label: string; href: string };
export type HeroVisual =
  | { kind: "shot"; shot: ShotId; panel?: "result" | "accounts" | "none" }
  | { kind: "workspace" }
  | { kind: "payout" }
  | { kind: "collage" }
  | { kind: "lens" }
  | { kind: "platforms" }
  | { kind: "company" }
  | { kind: "none" };

export type Block =
  | { type: "steps"; title: string; intro?: string; items: { title: string; body: string; visual?: "references" | "scan" | "seconds" | "invite" | "connect" | "post" | "earnings" }[] }
  | { type: "signals"; title: string; intro?: string; items: { key: string; title: string; body: string }[] }
  | { type: "features"; title: string; intro?: string; items: { title: string; body: string }[] }
  | { type: "links"; title: string; intro?: string; items: NavItem[] }
  | { type: "stats"; title: string; note?: string }
  | { type: "terms"; title: string; intro?: string; rows: [string, string][] }
  | { type: "example"; title: string; body: string; shot: ShotId }
  | { type: "split"; title: string; body: string; shot: ShotId; points?: string[] }
  | { type: "workspace"; title: string; body: string; points?: string[] }
  | { type: "scopes"; title: string; intro: string }
  | { type: "faq"; title: string; items: [string, string][] }
  | { type: "routes"; items: { title: string; body: string; cta: Cta }[] }
  | { type: "facts"; title: string; rows: [string, string][] }
  | { type: "cta"; title: string; body?: string; primary: Cta; secondary?: Cta };

export type Page = {
  path: string;
  section: Section;
  meta: { title: string; description: string };
  hero: { eyebrow: string; title: string; lead: string; primary?: Cta; secondary?: Cta; visual: HeroVisual; centered?: boolean };
  blocks: Block[];
};

const brandCta: Cta = { label: "Request access", href: "/request-access?role=brand" };
const creatorCta: Cta = { label: "Join the invite list", href: "/request-access?role=creator" };

const closingCta: Block = {
  type: "cta", title: "Measure your next creator campaign by the second.",
  body: "Tell us about the brand, the creators and the dates.",
  primary: brandCta, secondary: { label: "Contact us", href: "/contact" },
};

export const pages: Record<string, Page> = {
  platform: {
    path: "/platform", section: "product",
    meta: { title: "Platform | Fitted", description: "Computer vision, campaign management, creator payouts and platform integrations in one place." },
    hero: {
      eyebrow: "Platform overview", title: "One platform from brief to payout.",
      lead: "Brands say what to look for. Creators post as usual. Fitted measures each post and works out what it earned.",
      primary: brandCta, secondary: { label: "See computer vision", href: "/platform/computer-vision" },
      visual: { kind: "collage" },
    },
    blocks: [
      { type: "links", title: "What's in Fitted", items: nav.product.slice(1) },
      { type: "steps", title: "How a campaign runs", items: [
        { title: "Create a campaign", body: "Set the budget and dates, and upload the logos and products Fitted should look for.", visual: "references" },
        { title: "Invite creators", body: "Invite the creators you choose. They connect their accounts once.", visual: "invite" },
        { title: "Fitted analyzes each post", body: "When a creator posts, Fitted checks the video frame by frame for your brand.", visual: "scan" },
        { title: "Payouts follow exposure", body: "Each payout comes from the seconds your brand was on screen and the post's views.", visual: "earnings" },
      ] },
      { type: "stats", title: "Fitted's detection numbers to date" },
      closingCta,
    ],
  },
  computerVision: {
    path: "/platform/computer-vision", section: "product",
    meta: { title: "Computer vision | Fitted", description: "How Fitted measures the seconds your logos and products are on screen in creator videos." },
    hero: {
      eyebrow: "Computer vision", title: "See every second your brand is on screen.",
      lead: "Fitted checks every frame of a sponsored post for your logos and products, then adds up the seconds they are visible. Each post gets an exposure result you can audit.",
      primary: brandCta, secondary: { label: "See how payouts work", href: "/platform/creator-payouts" },
      visual: { kind: "lens" },
    },
    blocks: [
      { type: "steps", title: "From reference image to seconds on screen", intro: "Fitted runs these steps on every post in a campaign.", items: [
        { title: "Upload reference images", body: "Add photos of your logos and products. Fitted uses them to recognize your brand in creator videos, including at odd angles and partly hidden.", visual: "references" },
        { title: "Fitted scans each frame", body: "Fitted checks every second of a post. An item like a sweatshirt gets a box, and the logo on it gets its own.", visual: "scan" },
        { title: "Seconds add up per post", body: "Fitted adds the detections up into seconds on screen for each post and shows them next to the post's views.", visual: "seconds" },
      ] },
      { type: "signals", title: "Three ways a brand shows up in a post",
        intro: "Each campaign can set its own rules, such as a minimum number of seconds on screen. Fitted marks which posts qualify.",
        items: [
          { key: "visual", title: "On screen", body: "Fitted finds your logos and products in the video frames." },
          { key: "audio", title: "Said out loud", body: "Fitted picks up your brand name when the creator says it." },
          { key: "caption", title: "In the caption", body: "Fitted reads your brand name and tags in the caption." },
        ] },
      { type: "stats", title: "Fitted's detection numbers to date" },
      { type: "faq", title: "Questions", items: [
        ["Which platforms does it work on?", "It works on Instagram posts and reels, TikTok videos and YouTube videos from creators who connect their accounts."],
        ["Does Fitted store creator videos?", "Fitted processes post media to measure exposure for the campaign. See the Privacy Policy for how long data is kept."],
        ["How accurate is detection?", "Detection accuracy is above 97 percent. Fitted has analyzed more than 10 million frames so far."],
      ] },
      closingCta,
    ],
  },
  campaignManager: {
    path: "/platform/campaign-manager", section: "product",
    meta: { title: "Campaign manager | Fitted", description: "Track every brand, campaign, creator and post in one workspace, with views, engagement and estimated media value." },
    hero: {
      eyebrow: "Campaign manager", title: "Every campaign and creator in one place.",
      lead: "Each brand gets a workspace with its campaigns, creators and posts. Views, engagement and estimated media value update as posts sync from the platforms.",
      primary: brandCta, secondary: { label: "See creator payouts", href: "/platform/creator-payouts" },
      visual: { kind: "workspace" },
    },
    blocks: [
      { type: "features", title: "What a brand workspace holds", items: [
        { title: "Campaigns", body: "Each campaign has its budget, dates and the logos and products to look for." },
        { title: "Creators and groups", body: "Invite creators and sort them into groups, such as a college team or a city." },
        { title: "Posts and stories", body: "Every campaign post syncs with its views and engagement, plus its exposure result." },
        { title: "Product gifting", body: "Creators request products, and brands with a Shopify store ship them through the Fitted app." },
        { title: "Estimated media value", body: "Each post gets a dollar value based on its views and engagement." },
      ] },
      { type: "workspace", title: "Reports you can share", body: "Filter by channel, campaign and date range, then share a link or export a PDF for the team or the client.",
        points: ["Views, engagements, estimated media value, total mentions and posts published over time", "Filters for channel, campaign and date range", "Share link and PDF export"] },
      closingCta,
    ],
  },
  creatorPayouts: {
    path: "/platform/creator-payouts", section: "product",
    meta: { title: "Creator payouts | Fitted", description: "Creator payouts based on verified seconds on screen and views, with terms each brand sets." },
    hero: {
      eyebrow: "Creator payouts", title: "Pay for the seconds people saw.",
      lead: "Each payout comes from the seconds a brand is on screen and the views the post earned. Brands set the terms, and every number traces back to a post.",
      primary: brandCta, secondary: { label: "For creators", href: "/solutions/creators" },
      visual: { kind: "payout" },
    },
    blocks: [
      { type: "example", title: "One post, start to finish", shot: "puma",
        body: "This post had Puma on screen for 12.4 seconds and earned 150k views. Fitted estimated its payout at $450.00." },
      { type: "features", title: "Brands set the terms", items: [
        { title: "You set when it pays", body: "After each post, or when the campaign ends." },
        { title: "You set the limits", body: "An optional minimum and maximum for each post." },
      ] },
      { type: "steps", title: "From post to payment", items: [
        { title: "The post goes live", body: "The creator posts to their connected account as part of the campaign." },
        { title: "Fitted measures it", body: "Fitted adds the seconds on screen and views to the post's result." },
        { title: "The payout unlocks", body: "After the post or when the campaign ends. The brand picks which." },
        { title: "The creator gets paid", body: "After the waiting period the brand set." },
      ] },
      { type: "faq", title: "Questions", items: [
        ["Who sets the rates?", "The brand sets the payout terms for its campaigns. Fitted works out each payout from those terms and the post's result."],
        ["Can a creator see how Fitted worked out a payout?", "Yes. Each post shows its seconds on screen, its views and what it earned."],
      ] },
      closingCta,
    ],
  },
  integrations: {
    path: "/platform/integrations", section: "product",
    meta: { title: "Integrations | Fitted", description: "How creators connect Instagram, TikTok and YouTube to Fitted, what Fitted can see and how to disconnect." },
    hero: {
      eyebrow: "Integrations", title: "Connect Instagram, TikTok and YouTube.",
      lead: "Creators link their own accounts through each platform's official sign-in. Fitted only sees the profile and posts it needs to measure a campaign.",
      primary: creatorCta, secondary: { label: "Privacy Policy", href: "/privacy-policy" },
      visual: { kind: "platforms" },
    },
    blocks: [
      { type: "scopes", title: "What Fitted can see", intro: "Fitted only reads. It never posts or changes anything." },
      { type: "features", title: "What Fitted never does", items: [
        { title: "Post for you", body: "Fitted has no permission to publish, edit or delete anything on your accounts." },
        { title: "Read your messages", body: "Fitted has no access to your messages or DMs." },
        { title: "Keep access after you leave", body: "Disconnect from your Fitted profile or the platform's settings, and Fitted stops syncing." },
      ] },
      { type: "faq", title: "Questions", items: [
        ["Do I need a business account?", "On Instagram, yes. It needs a business or creator account. TikTok and YouTube work with any account."],
        ["Which posts does Fitted measure?", "Fitted measures the posts you make for campaigns you joined. It looks through your public videos to find them."],
        ["How do I disconnect?", "Remove the account from your Fitted profile, or revoke Fitted in the platform's app permissions settings."],
      ] },
      { type: "cta", title: "Ready to connect?", body: "Applications for individual creators open soon.", primary: creatorCta, secondary: { label: "Contact us", href: "/contact" } },
    ],
  },
  brands: {
    path: "/solutions/brands", section: "solutions",
    meta: { title: "For brands | Fitted", description: "Pay creators for verified seconds on screen and views instead of flat fees." },
    hero: {
      eyebrow: "For brands", title: "Pay for visibility, not posts.",
      lead: "A flat fee pays for a post going live. Fitted measures how long your product is on screen and how many people watched, so your creator budget follows real exposure.",
      primary: { label: "Book a demo", href: "/request-access?role=brand" }, secondary: { label: "See the platform", href: "/platform" },
      visual: { kind: "shot", shot: "gatorade", panel: "result" },
    },
    blocks: [
      { type: "features", title: "What changes when you pay on exposure", items: [
        { title: "You see what each post delivered", body: "Each sponsored post shows its seconds on screen and its views." },
        { title: "Budget follows results", body: "Creators whose content puts your product on screen longer earn more." },
        { title: "No screenshots to chase", body: "Posts and views sync from Instagram, TikTok and YouTube." },
        { title: "Reports for the team", body: "Share a link or export a PDF of any brand or campaign." },
      ] },
      { type: "split", title: "Built for product placement", shot: "gatorade",
        body: "Fitted finds your logo even on a flag in the background of a race. Each detection counts toward the post's seconds on screen.",
        points: ["Logos and products, including partly hidden ones", "Spoken mentions and caption mentions", "Per-campaign rules, such as a minimum number of seconds"] },
      { type: "stats", title: "Fitted's detection numbers to date" },
      { type: "cta", title: "See Fitted on your own campaign.", body: "The demo is a 15 minute walkthrough of campaigns, approvals, reporting and payouts.", primary: { label: "Book a demo", href: "/request-access?role=brand" }, secondary: { label: "Contact us", href: "/contact" } },
    ],
  },
  agencies: {
    path: "/solutions/agencies", section: "solutions",
    meta: { title: "For agencies | Fitted", description: "Run creator campaigns for several brands from one Fitted account and show each client what their creators delivered." },
    hero: {
      eyebrow: "For agencies", title: "Every client's creators in one account.",
      lead: "Manage several brands, their campaigns and your creator roster from one place, and show each client what their creators delivered.",
      primary: { label: "Apply as a partner", href: "/request-access?role=talent" }, secondary: { label: "See the platform", href: "/platform" },
      visual: { kind: "shot", shot: "adidas", panel: "result" },
    },
    blocks: [
      { type: "features", title: "Why agencies use Fitted", items: [
        { title: "One login, many brands", body: "Switch between the brands you manage. Each has its own campaigns, creators and reports." },
        { title: "Groups for your roster", body: "Sort your creators into groups, and see each group's posts and results." },
        { title: "Results clients can check", body: "Every post shows its seconds on screen and views, so a report needs no explaining." },
        { title: "Campaigns for your talent", body: "We match partners with brand campaigns that pay on exposure." },
      ] },
      { type: "steps", title: "How the partner program works", items: [
        { title: "Tell us about your agency", body: "Share your contact details and how much talent you manage." },
        { title: "We review your submission", body: "Our partnerships team reviews every agency and gets in touch." },
        { title: "Bring your roster to campaigns", body: "Your creators join brand campaigns and get paid on exposure." },
      ] },
      { type: "cta", title: "Bring your roster to Fitted.", primary: { label: "Apply as a partner", href: "/request-access?role=talent" }, secondary: { label: "Contact us", href: "/contact" } },
    ],
  },
  creators: {
    path: "/solutions/creators", section: "solutions",
    meta: { title: "For creators | Fitted", description: "Get paid for the seconds a brand is on screen in your posts and the views they earn." },
    hero: {
      eyebrow: "For creators", title: "Get paid for each second on screen.",
      lead: "Brands on Fitted pay creators by how visible their product is in your content and how many people watch it. Post in your own style. The longer the product is on screen and the more people watch, the more the post earns.",
      primary: creatorCta, secondary: { label: "How payouts work", href: "/platform/creator-payouts" },
      visual: { kind: "shot", shot: "creator", panel: "accounts" },
    },
    blocks: [
      { type: "steps", title: "How it works for creators", intro: "From invite to payout.", items: [
        { title: "Join a campaign", body: "Accept an invite from a brand or an agency you work with.", visual: "invite" },
        { title: "Connect your accounts", body: "Link Instagram, TikTok or YouTube once. Fitted then pulls in your campaign posts and their views.", visual: "connect" },
        { title: "Post as you normally would", body: "There are no scripts. Feature the product your way.", visual: "post" },
        { title: "See your earnings", body: "Each post shows its seconds on screen, its views and what it earned.", visual: "earnings" },
      ] },
      { type: "scopes", title: "What Fitted can see", intro: "Fitted only reads. It never posts or changes anything." },
      { type: "cta", title: "Applications for individual creators open soon.", body: "Leave your details to hear first.", primary: creatorCta },
    ],
  },
  about: {
    path: "/about", section: "company",
    meta: { title: "About | Fitted", description: "Fitted AI, Inc. builds software that measures brand visibility in creator content and ties creator pay to it." },
    hero: {
      eyebrow: "About Fitted", title: "We measure what creators deliver.",
      lead: "Fitted AI, Inc. builds software that measures how visible a brand is in creator content and ties creator pay to it.",
      primary: { label: "Contact us", href: "/contact" }, secondary: { label: "See the platform", href: "/platform" },
      visual: { kind: "company" }, centered: true,
    },
    blocks: [
      { type: "features", title: "Why we built Fitted", items: [
        { title: "Flat fees hide results", body: "Brands spend a lot on creator content but mostly pay flat fees, with little proof of what a post delivered." },
        { title: "Flat fees pay everyone the same", body: "A creator whose post puts a product on screen for 12 seconds earns the same as one who shows it for 1." },
        { title: "Fitted measures it", body: "Fitted counts the seconds a brand is on screen and the views a post earns, and turns that into the payout." },
      ] },
      { type: "split", title: "Who we work with", shot: "adidas",
        body: "We work with brands that run creator and product placement campaigns, the agencies and talent managers who support them, and creators on Instagram, TikTok and YouTube.",
        points: ["Brands and their marketing teams", "Agencies and talent managers", "Creators on Instagram, TikTok and YouTube"] },
      { type: "features", title: "How we handle data", items: [
        { title: "Creators stay in control", body: "Creators connect their own accounts and can disconnect at any time." },
        { title: "Fitted only reads", body: "Fitted reads profiles and campaign posts. It never posts on anyone's behalf." },
        { title: "Only what campaigns need", body: "We use platform data to measure campaigns and pay creators, as the Privacy Policy describes." },
      ] },
      { type: "facts", title: "Company", rows: [["Legal name", COMPANY], ["Address", ADDRESS.join(", ")], ["Email", CONTACT_EMAIL], ["Platforms", "Instagram, TikTok, YouTube"]] },
    ],
  },
  contact: {
    path: "/contact", section: "company",
    meta: { title: "Contact | Fitted", description: "Contact Fitted AI, Inc. by email, book a demo or join the creator invite list." },
    hero: {
      eyebrow: "Contact", title: "Talk to the Fitted team.",
      lead: "Write to us about a campaign, a payout, a partnership or your data. A person on the team will reply.",
      visual: { kind: "none" }, centered: true,
    },
    blocks: [
      { type: "routes", items: [
        { title: "Brands and agencies", body: "Plan a campaign or see the platform.", cta: { label: "Book a demo", href: "/request-access?role=brand" } },
        { title: "Creators", body: "Join the invite list for upcoming campaigns.", cta: { label: "Join the list", href: "/request-access?role=creator" } },
        { title: "Support and privacy", body: "Account help, data access and deletion requests.", cta: { label: CONTACT_EMAIL, href: `mailto:${CONTACT_EMAIL}` } },
      ] },
      { type: "facts", title: "Company", rows: [["Legal name", COMPANY], ["Address", ADDRESS.join(", ")], ["Email", CONTACT_EMAIL], ["Already a customer", "Log in at app.fitted.co"]] },
    ],
  },
};

export const notFound = {
  title: "This page doesn't exist.",
  lead: "The link may be old or mistyped. These are the main pages.",
  links: [nav.product[0], nav.solutions[0], nav.solutions[2], nav.company[1]],
};
